import { FC, Fragment } from "react";
import { StyledEngineProvider } from "@mui/material/styles";
import { Provider } from "react-redux";
import { Router, Route, Switch } from "react-router-dom";
import KeyBinding from "react-keybinding-component";
import { createBrowserHistory } from "history";
import { Zendesk } from "./components/Zendesk";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { ThemeProvider as StyledThemeProvider } from "styled-components";

import store from "./state/store";

import LoginPage from "./pages/login/LoginPage";
import HomePage from "./pages/home/homePage";
import NotFoundPage from "./pages/error/NotFoundPage";
import BookingsPage from "./pages/bookings/BookingsPage";
import BookingViewPage from "./pages/booking/BookingView/BookingViewPage";
import BookingCreatePage from "./pages/booking/BookingCreate/BookingCreatePage";
import LocationsPage from "./pages/locations/LocationsPage";
import SettingsPage from "./pages/settings/SettingsPage";
import ComplaintsPage from "./pages/complaints/ComplaintsPage";
import ReportsPage from "./pages/reports/ReportsPage";
import ReportView from "./pages/reports/viewer/ReportView";
import KmosPage from "./pages/kmos/KmosPage";
import WorkflowDesigner from "./pages/workflow/WorkflowDesigner";
import { KeyManager } from "./pages/keys/KeyManager";
import KeyAuditView from "./pages/keys/keyAuditView/KeyAuditView";
import AdminPage from "./pages/admin/AdminPage";
import VehicleAuditView from "./pages/vehicles/vehicleAuditView/VehicleAuditView";
import ReportBuilder from "./pages/reports/builder/ReportBuilder";
import ExpressCheckOut from "./pages/expressCheckOut/expressCheckOut";
import InsightsPage from "./pages/insights/InsightsPage";
import { TenantSelectorPage } from "./pages/login/TenantSelectorPage";
import { VehicleManager } from "./pages/vehicles/VehicleManager";
import ReportsExPDPage from "./pages/reports_expd/ReportsExPDPage";
import DevicesPage from "./pages/mobile/devices/DevicesPage";
import WalkaroundsPage from "./pages/walkarounds/WalkaroundsPage";
import DriverShiftTrackerPage from "./pages/driverShiftTracker/DriverShiftTrackerPage";
import ScanningPage from "./pages/scanning/ScanningPage";

import RouteAuthenticated from "./components/routing/RouteAuthenticated";
import {
  CustomerLookUpProvider,
  CustomerLookUpDialog,
} from "./components/dialogs";

import usePaths from "./utility/paths";
import { Session } from "./utility/useSession";
import {
  ReleaseFlag,
  useReleaseManagement,
} from "./utility/feature-management";
import { options } from "./utility/options";
import useLocalizeDocumentAttributes from "./i18n/useLocalizeDocumentAttributes";
import { theme } from "./theme";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { LiveUpdatesProvider } from "./live-updates/LiveUpdatesProvider";
import { LiveUpdateDiagnosticsPanel } from "./live-updates/LiveUpdateDiagnosticsPanel";
import LocalPermissionsPanel from "./devtools/local-permissions/LocalPermissionsPanel";
import { LocalPermissionOverrideProvider } from "./devtools/local-permissions/LocalPermissionOverride";

declare module "@mui/material/styles" {
  interface Palette {
    brand: Palette["primary"];
    nav: Palette["primary"];
    border: Palette["primary"];
  }

  interface PaletteOptions {
    brand?: PaletteOptions["primary"];
    nav?: PaletteOptions["primary"];
    border?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    brand: true;
  }
}

export const history = createBrowserHistory();
const queryClient = new QueryClient();

export const Routes = () => {
  const paths = usePaths();
  useLocalizeDocumentAttributes();

  return (
    <Switch>
      <Route exact path={"/"} component={LoginPage} />
      <Route exact path={"/tenants"} component={TenantSelectorPage} />
      <RouteAuthenticated exact path={paths.home} component={HomePage} />
      <RouteAuthenticated
        exact
        path={paths.bookings}
        component={BookingsPage}
      />
      <RouteAuthenticated
        exact
        path={paths.booking(":bookingId")}
        component={BookingViewPage}
      />
      <RouteAuthenticated
        exact
        path={paths.bookingCreate}
        component={BookingCreatePage}
      />
      <RouteAuthenticated path={paths.locations} component={LocationsPage} />
      <RouteAuthenticated
        path={paths.expressCheckOut}
        component={ExpressCheckOut}
      />
      <RouteAuthenticated
        exact
        path={paths.complaints}
        component={ComplaintsPage}
      />
      <RouteAuthenticated
        path={paths.vehicleManager}
        component={VehicleManager}
      />
      <RouteAuthenticated path={paths.keyManager} component={KeyManager} />
      <RouteAuthenticated
        exact
        path={paths.keyManagerViewAudit(":auditId")}
        component={KeyAuditView}
      />
      <RouteAuthenticated
        exact
        path={paths.vehicleManagerViewAudit(":auditId")}
        component={VehicleAuditView}
      />
      <RouteAuthenticated exact path={paths.kmos} component={KmosPage} />
      <RouteAuthenticated
        exact
        path={paths.reportsWithCategory(":category")}
        component={ReportsPage}
      />
      <RouteAuthenticated
        exact
        path={paths.reportsV2WithCategory(":category")}
        component={ReportsExPDPage}
      />
      <RouteAuthenticated exact path={paths.reports} component={ReportsPage} />
      <RouteAuthenticated
        exact
        path={paths.reportsV2}
        component={ReportsExPDPage}
      />
      <RouteAuthenticated
        exact
        path={paths.reportV2View(":reportId", false)}
        component={ReportsExPDPage}
      />
      <RouteAuthenticated
        exact
        path={paths.reportV2View(":reportId", true)}
        component={ReportsExPDPage}
      />
      <RouteAuthenticated
        exact
        path={paths.workflow(":productCode")}
        component={WorkflowDesigner}
      />
      <RouteAuthenticated
        exact
        path={paths.reportEdit(":reportId")}
        component={ReportBuilder}
      />
      <RouteAuthenticated
        exact
        path={paths.reportView(":reportId")}
        component={ReportView}
      />
      <RouteAuthenticated
        exact
        path={paths.insights(":pageId")}
        component={InsightsPage}
      />
      <RouteAuthenticated
        exact
        path={paths.settings}
        component={SettingsPage}
      />
      <RouteAuthenticated exact path={paths.devices} component={DevicesPage} />
      <RouteAuthenticated exact path={paths.admin} component={AdminPage} />
      <RouteAuthenticated
        exact
        path={paths.busWalkarounds}
        component={WalkaroundsPage}
      />
      <RouteAuthenticated
        exact
        path={paths.driverShiftTracker}
        component={DriverShiftTrackerPage}
      />
      <RouteAuthenticated
        exact
        path={paths.scanning}
        component={ScanningPage}
      />
      <Route component={NotFoundPage} />
    </Switch>
  );
};

const AppKeys = () => {
  const paths = usePaths();
  const isVehicleMoveUiReleased = useReleaseManagement(
    ReleaseFlag.vehicleMoveUi,
  );

  if (!isVehicleMoveUiReleased) {
    return <></>;
  }

  const onKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case "F8":
        history.push(paths.vehicleManagerMove);
        break;
      case "F9":
        history.push(paths.keyManagerMove);
        break;
    }
  };

  return <KeyBinding onKey={onKeyDown} />;
};

const ZENDESK_KEY = options.zendeskKey;

const PermissionOverrideBoundary = import.meta.env.DEV
  ? LocalPermissionOverrideProvider
  : Fragment;

export const App: FC = () => (
  <StyledEngineProvider injectFirst>
    <MuiThemeProvider theme={theme}>
      <StyledThemeProvider theme={theme}>
        <Router history={history}>
          <Session>
            <LiveUpdatesProvider>
              <QueryClientProvider client={queryClient}>
                <PermissionOverrideBoundary>
                  <Provider store={store}>
                    <CustomerLookUpProvider>
                      <AppKeys />
                      <CustomerLookUpDialog />
                      <Routes />
                      <TanStackDevtools
                        plugins={[
                          {
                            name: "TanStack Query",
                            render: <ReactQueryDevtoolsPanel />,
                            defaultOpen: false,
                          },
                          {
                            name: "Live updates",
                            render: (_element, { devtoolsOpen }) => (
                              <LiveUpdateDiagnosticsPanel active={devtoolsOpen} />
                            ),
                            defaultOpen: false,
                          },
                          ...(import.meta.env.DEV
                            ? [
                                {
                                  name: "Local permissions",
                                  render: <LocalPermissionsPanel />,
                                  defaultOpen: false,
                                },
                              ]
                            : []),
                        ]}
                      />
                      {ZENDESK_KEY && (
                        <Zendesk
                          zendeskKey={ZENDESK_KEY}
                          onLoaded={() => console.log("Zendesk loaded")}
                        />
                      )}
                    </CustomerLookUpProvider>
                  </Provider>
                </PermissionOverrideBoundary>
              </QueryClientProvider>
            </LiveUpdatesProvider>
          </Session>
        </Router>
      </StyledThemeProvider>
    </MuiThemeProvider>
  </StyledEngineProvider>
);
